import type {
    AudioClassificationWindow,
} from "@mrflark/spl_sdk";
import type {
    MediaItem,
} from "@mrflark/spl_service_model";
import {
    useQuery,
} from "@tanstack/react-query";
import type {
    ReactElement,
} from "preact/compat";

import {
    splClient,
} from "../../api/client.ts";

type AudioClassificationStripProps = {
    currentItem: MediaItem | null;
};

// matches the @mrflark/ui dark theme tailwind palette. each classification
// gets its own hue so the strip reads at-a-glance without a legend.
const CLASSIFICATION_COLORS: Record<string, string> = {
    music: "bg-blue-500",
    speech: "bg-emerald-500",
    silence: "bg-zinc-500",
};

const CLASSIFICATION_LABELS: Record<string, string> = {
    music: "Music",
    speech: "Speech",
    silence: "Silence",
};

const formatTimestamp = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remaining = Math.floor(seconds % 60);
    return `${minutes}:${remaining.toString().padStart(2, "0")}`;
};

export const AudioClassificationStrip = ({
    currentItem,
}: AudioClassificationStripProps): ReactElement | null => {
    const isVideo = currentItem?.media_type === "video";

    const {
        data,
    } = useQuery({
        queryKey: ["audioClassifications", currentItem?.id],
        queryFn: () => splClient.getAudioClassifications(currentItem?.id ?? ""),
        enabled: isVideo && !!currentItem?.id,
        staleTime: 5 * 60 * 1000,
    });

    if (!isVideo || !data || data.windows_analyzed === 0) {
        return null;
    }

    // strip width is normalized to the total span. each window's flex-grow
    // is its duration so windows of different lengths render proportionally
    // (the trailing window is usually shorter than 10s).
    const totalDuration = data.classifications.reduce(
        (sum, window) => sum + (window.end_seconds - window.start_seconds),
        0,
    );

    return (
        <div className="mt-4 w-full max-w-3xl">
            <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-2">
                Audio
            </h3>
            <div className="flex w-full h-8 rounded-md overflow-hidden border border-border">
                {data.classifications.map(
                    (window: AudioClassificationWindow) => {
                        const duration = window.end_seconds -
                            window.start_seconds;
                        const widthPercent = totalDuration > 0
                            ? (duration / totalDuration) * 100
                            : 0;
                        const color =
                            CLASSIFICATION_COLORS[window.classification] ??
                                "bg-zinc-500";
                        return (
                            <div
                                key={window.start_seconds}
                                className={`${color} relative flex items-end`}
                                style={{
                                    width: `${widthPercent}%`,
                                }}
                                title={`${
                                    CLASSIFICATION_LABELS[
                                        window.classification
                                    ] ??
                                        window.classification
                                } · ${formatTimestamp(window.start_seconds)}–${
                                    formatTimestamp(window.end_seconds)
                                } · confidence ${window.confidence.toFixed(2)}`}
                            >
                                <div
                                    className="w-full bg-white/20"
                                    style={{
                                        height: `${window.confidence * 100}%`,
                                    }}
                                />
                            </div>
                        );
                    },
                )}
            </div>
            <div className="flex gap-3 mt-2 text-xs text-text-secondary">
                {(["music", "speech", "silence"] as const).map((kind) => (
                    <div key={kind} className="flex items-center gap-1">
                        <span
                            className={`inline-block w-3 h-3 rounded-sm ${
                                CLASSIFICATION_COLORS[kind]
                            }`}
                        />
                        <span>{CLASSIFICATION_LABELS[kind]}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};
